import type { Bula } from "@vetbolso/contracts";
import { dosesDaBula, espMatch, faixa, nomeEspecie } from "@vetbolso/core";
import { useState } from "react";
import { Busca, Cabeca, Cartao, combina, Destaque, Grupo, SemResultado, SpPill, Vazio } from "../comp.tsx";
import { useApp, useBase } from "../estado.tsx";
import "../bula.css";

const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;

/** Lista do bulário: fármacos da espécie atual por classe; os demais ao fim. */
export function TelaBulario() {
  const base = useBase();
  const { especie: e, abrir } = useApp();
  const [q, setQ] = useState("");
  const [classe, setClasse] = useState("");
  const termo = q.trim();
  const acha = (b: Bula) => combina(q, b.farmaco, b.classe, ...(b.apresentacoes ?? []).map((a) => a.nome));
  const todas = [...base.bulario].sort((a, b) => a.farmaco.localeCompare(b.farmaco, "pt-BR"));
  const classes = [...new Set(todas.map((b) => b.classe))].sort((a, b) => a.localeCompare(b, "pt-BR"));
  const lista = todas.filter((b) => acha(b) && (!classe || b.classe === classe));
  const daEspecie = lista.filter((b) => espMatch(b, e));
  const outras = lista.filter((b) => !espMatch(b, e));
  const grupos = new Map<string, Bula[]>();
  for (const b of daEspecie) grupos.set(b.classe, [...(grupos.get(b.classe) ?? []), b]);

  const cartao = (b: Bula) => {
    const doses = dosesDaBula(base.doses, b.id);
    const d = doses.find((x) => x.especie === e);
    const produtos = new Map((b.apresentacoes ?? []).filter((a) => a.uso).map((a) => [a.nome, a.uso]));
    const cobre = [
      b.indicacoes?.length ? plural(b.indicacoes.length, "indicação", "indicações") : "",
      b.interacoes?.length ? plural(b.interacoes.length, "interação", "interações") : "",
      b.efeitos?.length ? plural(b.efeitos.length, "efeito", "efeitos") : "",
    ].filter(Boolean);
    return (
      <Cartao
        key={b.id}
        icone="pill"
        tom="dose"
        titulo={b.farmaco}
        pills={b.especies.filter((x) => x !== "todas").map((x) => <SpPill key={x} id={x} />)}
        aoTocar={() => abrir({ t: "bula", id: b.id })}
      >
        {d && !d.semDose ? (
          <Destaque
            icone="syringe"
            v={
              <>
                <b>{faixa(d.min ?? 0, d.max, 3)}</b> {d.un}
              </>
            }
            q={[nomeEspecie(e), d.via, d.intervalo].filter(Boolean).join(" · ")}
          />
        ) : null}
        {produtos.size ? (
          <span className="item-chips">
            {[...produtos].map(([nome, uso]) => (
              <span key={nome} className="bula-prod-chip">
                {nome}
                <span className={`etq ${uso === "humano" ? "atencao" : "bom"}`}>{uso === "humano" ? "humano" : "vet"}</span>
              </span>
            ))}
          </span>
        ) : null}
        {cobre.length ? <span className="item-pe">{cobre.join(" · ")}</span> : null}
      </Cartao>
    );
  };

  const grupo = (titulo: string, bs: Bula[]) => (
    <Grupo key={titulo} titulo={titulo} n={bs.length}>
      {bs.map(cartao)}
    </Grupo>
  );

  return (
    <>
      <Cabeca titulo="Bulário" sub="Indicações, contraindicações, interações e efeitos" />
      <Busca valor={q} aoMudar={setQ} placeholder="Fármaco, classe ou nome comercial" rotulo="Buscar no bulário" />

      {classes.length > 1 ? (
        <fieldset className="bula-classes" aria-label="Filtrar por classe">
          <button type="button" className="chip" aria-pressed={!classe} onClick={() => setClasse("")}>
            Todas
          </button>
          {classes.map((c) => (
            <button key={c} type="button" className="chip" aria-pressed={classe === c} onClick={() => setClasse(classe === c ? "" : c)}>
              {c}
            </button>
          ))}
        </fieldset>
      ) : null}

      {[...grupos].sort(([a], [b]) => a.localeCompare(b, "pt-BR")).map(([g, bs]) => grupo(g, bs))}
      {outras.length ? grupo(`Sem uso descrito em ${nomeEspecie(e).toLowerCase()}`, outras) : null}

      {!lista.length ? (
        termo ? (
          <SemResultado termo={q} />
        ) : (
          <Vazio icone="livroAberto">O bulário ainda não tem fármacos publicados nesta versão.</Vazio>
        )
      ) : null}
    </>
  );
}
